"use client"

import { notFound } from "next/navigation"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CoverageChecker } from "@/components/insurance/coverage-checker"
import { preselectCoveragePackage } from "@/components/insurance/coverage-preselect"
import {
  InsuranceClosingCta,
  InsuranceJourneySection,
  InsurancePhotoHero,
  SubtitleWithTermsLink,
} from "@/components/insurance/insurance-page-sections"
import { ArrowRightIcon } from "@/components/ui/button"
import { FaqSection } from "@/components/ui/faq-section"
import { InsurerExamples } from "@/components/ui/insurer-examples"
import { useCountry } from "@/components/layout/country-provider"
import { insuranceKeys as k } from "@/i18n/insurance-keys"
import {
  INSURER_PAGES,
  type InsurerPageSlug,
} from "@/lib/insurance/insurer-pages"
import { insurerPagePackages } from "@/lib/insurance/insurer-page-coverage"
import type { InsurerData, Locale } from "@/lib/insurance/insurance-types"
import { useLocalePath } from "@/lib/use-localized-navigation"

/* ───────────────────── Page ───────────────────── */

export default function InsurerPageClient({
  slug,
  initialFaqs,
  initialInsurerData,
}: {
  slug: InsurerPageSlug
  initialFaqs: { question: string; answer: string }[]
  initialInsurerData?: InsurerData | null
}) {
  const { code: country } = useCountry()
  // Same gate as /insurance: Swiss insurers only. NL visitors see 404.
  if (country === "nl") notFound()

  const page = INSURER_PAGES[slug]
  const insurer = page.name
  const t = useTranslations("insurance")
  const tp = useTranslations("insurancePages")
  const lp = useLocalePath()
  const locale = useLocale()

  // Packages, their names and what each pays back all come from the CMS, so
  // the cards can't drift from the checker above them.
  const packages = insurerPagePackages(
    initialInsurerData ?? null,
    page.insurerId,
    locale as Locale
  )
  // Insurers that reimburse nothing get an honest hero rather than the
  // "may cover your check-up" one: `confirmedNoCoverage` insurers (Helsana,
  // Assura, Concordia, ÖKK, Sanitas, Sympany) would otherwise promise a
  // reimbursement the checker immediately contradicts.
  const reimburses = packages.some((pkg) => !pkg.coversNothing)

  const JOURNEY_STEPS = [
    {
      title: t(k.step1Title),
      description: tp("step1Description", { insurer }),
    },
    { title: t(k.step2Title), description: t(k.step2Description) },
    { title: t(k.step3Title), description: t(k.step3Description) },
    {
      title: t(k.step4Title),
      description: tp("step4Description", { insurer }),
    },
  ]

  return (
    <>
      <InsurancePhotoHero
        heading={tp("heroHeading", { insurer })}
        subtitle={
          reimburses ? (
            <SubtitleWithTermsLink
              text={tp("heroSubtitle", { insurer })}
              linkLabel={tp("termsLink")}
              href={lp("/insurance-checker-terms")}
            />
          ) : (
            tp("heroSubtitleNoCoverage", { insurer })
          )
        }
      />

      {/* Coverage checker embed — pulled up over the hero band, with this
          page's insurer already selected. */}
      <section
        className="relative z-10 -mt-32 scroll-mt-24 pb-16 md:-mt-[200px] md:scroll-mt-28"
        id="coverage-form"
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <CoverageChecker
            data={initialInsurerData ?? null}
            tone="tinted"
            initialInsurerId={page.insurerId}
          />
        </div>
      </section>

      <InsuranceJourneySection
        heading={t(k.howItWorksHeading)}
        steps={JOURNEY_STEPS}
      />

      {/* The insurer's supplementary packages — what each is built for, with
          a button that selects it in the checker above. */}
      {packages.length > 0 && (
        <section className="py-16">
          {/* Same shell as the insurer examples below (1440 max, 2 columns,
              24px gutter), so the two card sections line up. */}
          <div className="mx-auto max-w-[1440px] px-4 md:px-10">
            <h2 className="type-display-sm text-neutral-900">
              {tp("packagesHeading", { insurer })}
            </h2>
            <p className="type-body-lg mt-4 max-w-[760px] text-neutral-600">
              {tp("packagesIntro")}
            </p>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
              {packages.map((pkg) => {
                const name = `${insurer} ${pkg.name}`
                const proseKey = `insurers.${slug}.packages.${pkg.id}`
                return (
                  <div
                    key={pkg.id}
                    className="flex h-full flex-col gap-6 rounded-[16px] bg-white p-6 md:pt-10 md:pr-[60px] md:pb-10 md:pl-10"
                  >
                    <div>
                      <h3 className="type-title-lg text-neutral-900">{name}</h3>
                      {/* Written per package for all fourteen insurers. A
                          package whose prose is missing renders its title and
                          CTA alone rather than a gap. */}
                      {tp.has(proseKey as never) && (
                        <p className="type-body-lg mt-3 text-neutral-600">
                          {tp(proseKey as never)}
                        </p>
                      )}
                    </div>
                    {/* No figures on the card at all (Nadjib, 2026-08-21):
                        neither per-check-up amounts nor a "doesn't reimburse"
                        line. These cards exist to explain what each package is
                        for, for search and ads; the reimbursement question is
                        answered for the reader's own policy by the checker
                        above, which is where the CTA below sends them. Every
                        package therefore reads the same way whether or not it
                        pays anything back. */}
                    {/* A text link, not a button: the package name is
                        already the card's heading, and repeating it in a
                        pill ("Check CSS myFlex Premium Hospitalisation
                        Insurance") overflowed the card. */}
                    <button
                      type="button"
                      className="group type-body-md text-aubergine-800 hover:text-aubergine-900 mt-auto inline-flex w-fit cursor-pointer items-center gap-2 font-medium transition-colors"
                      onClick={() =>
                        preselectCoveragePackage({
                          insurerId: page.insurerId,
                          packageId: pkg.id,
                        })
                      }
                    >
                      {tp("checkPackage")}
                      <ArrowRightIcon size={20} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* Real-world examples — the other insurers, so this page links back
          across the hub. */}
      <InsurerExamples coveragePageCopy />

      {/* FAQ */}
      <FaqSection
        heading={t(k.faqHeading)}
        linkLabel={t(k.readAllFaqs)}
        linkHref={lp("/faq")}
        items={initialFaqs}
        currentPage="insurance"
        layout="stacked"
      />

      <InsuranceClosingCta
        heading={t(k.ctaHeading)}
        subtitle={t(k.ctaSubtitle)}
        primaryLabel={t(k.bookCheckup)}
        primaryHref={lp("/pricing")}
        secondaryLabel={t(k.talkToTeam)}
        secondaryHref={lp("/contact")}
      />
    </>
  )
}
